import { ScrollView, Platform } from "react-native";
import { UnistylesRuntime } from "react-native-unistyles";
import { Redirect } from "expo-router";
import { SimpleGrid, Text, View } from "@src/components";
import { Button } from "@src/components/Button";
import { AppSpacing, colorPalettes } from "@src/unistyles";
import { useState } from "react";
import { useIsOnline } from "@src/utils/network";
import { isProductionEnvironment } from "@src/utils/environment";

export default function ThemeDemoScreen() {
  const { toggle } = useTheme();
  const isOnline = useIsOnline();

  // Internal debug tooling — must never be reachable in the production app.
  if (isProductionEnvironment()) {
    return <Redirect href="/not-found" />;
  }

  return (
    <View flex={1} bg="background">
      <ScrollView style={{ flex: 1 }} contentContainerStyle={{ padding: 20 }}>
        <View mb="6xl" style={{ paddingTop: Platform.OS === "ios" ? 60 : 40 }}>
          <Text textStyle="4xl" fontWeight="bold">
            Theme Demo
          </Text>
        </View>

        <View mb="8xl">
          <Text textStyle="xl" fontWeight="bold" mb="4xl">
            Network Status
          </Text>
          <View mb="md">
            <Text>{isOnline ? "Online ✅" : "Offline ❌"}</Text>
          </View>
        </View>

        <View mb="8xl">
          <Text textStyle="xl" fontWeight="bold" mb="4xl">
            Theme Controls
          </Text>
          <Button label="Theme toggle" size="md" variant="primary" variantColor="blue" onPress={() => toggle()} />
        </View>

        <View mb="8xl">
          <Text textStyle="xl" fontWeight="bold" mb="4xl">
            Typography
          </Text>

          <Text textStyle="4xl" fontWeight="bold" mb="md">
            Title Text
          </Text>
          <Text textStyle="xl" fontWeight="bold" mb="md">
            Subtitle Text
          </Text>
          <Text mb="md">Default Text</Text>
          <Text color="navy" mb="md">
            Link Text
          </Text>
        </View>

        <View mb="8xl">
          <Text textStyle="xl" fontWeight="bold" mb="4xl">
            Button Sizes
          </Text>

          <View gap="md" mb="4xl">
            <Text fontWeight="600" mb="sm">
              Solid
            </Text>
            <View flexDirection="row" gap="sm" flexWrap="wrap" alignItems="center">
              <Button label="Small" size="sm" variant="primary" variantColor="blue" onPress={() => {}} />
              <Button label="Medium" size="md" variant="primary" variantColor="blue" onPress={() => {}} />
              <Button label="Large" size="lg" variant="primary" variantColor="blue" onPress={() => {}} />
            </View>
          </View>

          <View gap="md" mb="4xl">
            <Text fontWeight="600" mb="sm">
              Button Variants (Navy)
            </Text>
            <View flexDirection="row" gap="sm" flexWrap="wrap">
              <Button label="Primary" size="md" variant="primary" variantColor="navy" onPress={() => {}} />
              <Button label="Secondary" size="md" variant="secondary" variantColor="navy" onPress={() => {}} />
              <Button label="Link" size="md" variant="link" variantColor="navy" onPress={() => {}} />
            </View>
          </View>

          <View gap="md" mb="4xl">
            <Text fontWeight="600" mb="sm">
              Button Variants (Blue)
            </Text>
            <View flexDirection="row" gap="sm" flexWrap="wrap">
              <Button label="Primary" size="md" variant="primary" variantColor="blue" onPress={() => {}} />
              <Button label="Secondary" size="md" variant="secondary" variantColor="blue" onPress={() => {}} />
              <Button label="Link" size="md" variant="link" variantColor="blue" onPress={() => {}} />
            </View>
          </View>

          <View gap="md" mb="4xl">
            <Text fontWeight="600" mb="sm">
              Primary Colors
            </Text>
            <View flexDirection="row" gap="sm" flexWrap="wrap">
              <Button label="Navy" size="md" variant="primary" variantColor="blue" onPress={() => {}} />
              <Button label="Red" size="md" variant="primary" variantColor="red" onPress={() => {}} />
              <Button label="Blue" size="md" variant="primary" variantColor="blue" onPress={() => {}} />
              <Button label="Grey" size="md" variant="primary" variantColor="grey" onPress={() => {}} />
            </View>
          </View>

          <View gap="md" mb="4xl">
            <Text fontWeight="600" mb="sm">
              Secondary Colors
            </Text>
            <View flexDirection="row" gap="sm" flexWrap="wrap">
              <Button label="Cyan" size="md" variant="primary" variantColor="cyan" onPress={() => {}} />
              <Button label="Purple" size="md" variant="primary" variantColor="purple" onPress={() => {}} />
            </View>
          </View>

          <View gap="md" mb="4xl">
            <Text fontWeight="600" mb="sm">
              System Colors
            </Text>
            <View flexDirection="row" gap="sm" flexWrap="wrap">
              <Button label="Danger" size="md" variant="primary" variantColor="danger" onPress={() => {}} />
              <Button label="Success" size="md" variant="primary" variantColor="success" onPress={() => {}} />
              <Button label="Warning" size="md" variant="primary" variantColor="warning" onPress={() => {}} />
            </View>
          </View>

          <View gap="md" mb="4xl">
            <Text fontWeight="600" mb="sm">
              Destructive Actions
            </Text>
            <Text color="textMuted" textStyle="sm/regular" mb="sm">
              Using system danger color (error)
            </Text>
            <View flexDirection="row" gap="sm" flexWrap="wrap">
              <Button label="Delete" size="md" variant="primary" variantColor="danger" onPress={() => {}} />
              <Button label="Remove" size="md" variant="secondary" variantColor="danger" onPress={() => {}} />
            </View>
          </View>

          <View gap="md" mb="4xl">
            <Text fontWeight="600" mb="sm">
              Data Visualisation
            </Text>
            <View flexDirection="row" gap="sm" flexWrap="wrap">
              <Button label="Indigo" size="md" variant="primary" variantColor="indigo" onPress={() => {}} />
              <Button label="Teal" size="md" variant="primary" variantColor="teal" onPress={() => {}} />
            </View>
          </View>

          <View gap="md">
            <Text fontWeight="600" mb="sm">
              Loading States
            </Text>
            <View flexDirection="row" gap="sm" flexWrap="wrap">
              <Button size="md" variant="primary" variantColor="grey" loading={true} onPress={() => {}} />
              <Button
                size="md"
                variant="secondary"
                variantColor="grey"
                loading={true}
                loadingText="Saving..."
                onPress={() => {}}
              />
              <Button size="md" variant="link" variantColor="grey" loading={true} onPress={() => {}} />
            </View>
          </View>
        </View>

        <View mb="8xl">
          <Text textStyle="xl" fontWeight="bold" mb="4xl">
            Color Palette
          </Text>

          <View flexDirection="row" flexWrap="wrap" gap="4xl">
            {Object.keys(colorPalettes).map((key) => (
              <View key={key}>
                <Text mb="sm" textTransform="capitalize">
                  {key}
                </Text>
                <SimpleGrid columns={3} spacing="sm">
                  {Object.entries(colorPalettes[key as keyof typeof colorPalettes]).map(([num, hex]) => (
                    <ColorSwatch key={key + num + hex} name={num} color={hex} />
                  ))}
                </SimpleGrid>
              </View>
            ))}
          </View>
        </View>

        <View mb="8xl">
          <Text textStyle="xl" fontWeight="bold" mb="4xl">
            Spacing System
          </Text>

          <View gap="xl">
            {Object.entries(UnistylesRuntime.getTheme().spacing).map(([key, value]) => (
              <View key={key} p={key as AppSpacing} bg="navy" borderWidth={2}>
                <Text>
                  {key} = {value}px
                </Text>
              </View>
            ))}
          </View>
        </View>
      </ScrollView>
    </View>
  );
}

interface ColorSwatchProps {
  name: string;
  color: string;
}

function ColorSwatch({ name, color }: ColorSwatchProps) {
  return (
    <View alignItems="center" gap="xs">
      <View h={60} w="100%" style={{ backgroundColor: color }} borderWidth={1} />
      <Text fontSize={11} textAlign="center">
        {name}
      </Text>
    </View>
  );
}

function useTheme() {
  const [theme, setActiveTheme] = useState<"light" | "dark">(UnistylesRuntime.themeName || "light");

  const setTheme = (value: "light" | "dark") => {
    if (UnistylesRuntime.hasAdaptiveThemes) {
      UnistylesRuntime.setAdaptiveThemes(false);
    }
    console.log({ value });
    UnistylesRuntime.setTheme(value);
    setActiveTheme(UnistylesRuntime.themeName as "light" | "dark");
  };

  const toggle = () => {
    const newTheme = theme === "light" ? "dark" : "light";
    setTheme(newTheme);
  };

  return {
    theme,
    toggle,
    setTheme,
  };
}
